import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DEFAULT_TOAST_DURATION } from './constants';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import LoadingSpinner from './components/LoadingSpinner';

// =============================================================================
// LAZY-LOADED COMPONENTS (Code Splitting)
// =============================================================================
// Cada componente se carga solo cuando se necesita, reduciendo el bundle inicial

// Auth Components
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

// App Components
const Dashboard = lazy(() => import('./components/Dashboard'));
const PaymentsList = lazy(() => import('./components/PaymentsList'));
const NotificationHistory = lazy(() => import('./components/NotificationHistory'));
const Channels = lazy(() => import('./components/Channels'));
const FilesManager = lazy(() => import('./components/FilesManager'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const Profile = lazy(() => import('./components/Profile'));
const Templates = lazy(() => import('./components/Templates'));
const TemplateTypes = lazy(() => import('./components/TemplateTypes'));
const TemplateLibraryPage = lazy(() => import('./components/TemplateLibraryPage'));
const ApiDocumentation = lazy(() => import('./components/ApiDocumentation'));
const SystemDocumentation = lazy(() => import('./components/SystemDocumentation'));
const PublisherSystems = lazy(() => import('./components/PublisherSystems'));
const Billers = lazy(() => import('./components/Billers'));
const BillerDashboard = lazy(() => import('./components/BillerDashboard'));
const NotificationVectors = lazy(() => import('./components/NotificationVectors'));
const PurgeManagement = lazy(() => import('./components/PurgeManagement'));
const Prices = lazy(() => import('./components/Prices'));
const Logs = lazy(() => import('./components/Logs'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const HistoricPayments = lazy(() => import('./components/HistoricPayments'));
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const CustomersManagement = lazy(() => import('./components/CustomersManagement'));
const AdvancedScoreDashboard = lazy(() => import('./components/AdvancedScoreDashboard'));
const AlertsPanel = lazy(() => import('./components/AlertsPanel'));
const AdvancedConfigPanel = lazy(() => import('./components/AdvancedConfigPanel'));
const IndustryManagement = lazy(() => import('./components/IndustryManagement'));
const NegotiationPlansManager = lazy(() => import('./components/NegotiationPlansManager'));
const NegotiationCampaignDashboard = lazy(() => import('./components/NegotiationCampaignDashboard'));
const AcceptNegotiationPlan = lazy(() => import('./components/AcceptNegotiationPlan'));
const RoleManagement = lazy(() => import('./components/RoleManagement'));
const AuditLogsViewer = lazy(() => import('./components/AuditLogsViewer'));
const AuditStatsDashboard = lazy(() => import('./components/AuditStatsDashboard'));
const SecurityEventsPanel = lazy(() => import('./components/SecurityEventsPanel'));
const DefaultRedirect = lazy(() => import('./components/DefaultRedirect'));
const PublisherThemeManager = lazy(() => import('./components/PublisherThemeManager'));
const MonitoringDashboard = lazy(() => import('./components/MonitoringDashboard'));
const PublisherUserManagement = lazy(() => import('./components/PublisherUserManagement')); // v3.1.0
const BillerUserManagement = lazy(() => import('./components/BillerUserManagement')); // v3.1.0
const PublisherDashboard = lazy(() => import('./components/PublisherDashboard')); // v3.1.0

// =============================================================================
// WRAPPER COMPONENT FOR SUSPENSE
// =============================================================================
// Envuelve cada ruta con Suspense para manejar el loading de componentes lazy
const SuspenseRoute = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <Router>
          <ThemeProvider>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <SuspenseRoute>
                    <Login />
                  </SuspenseRoute>
                } />
                <Route path="/register" element={
                  <SuspenseRoute>
                    <Register />
                  </SuspenseRoute>
                } />
                <Route path="/verify-email" element={
                  <SuspenseRoute>
                    <VerifyEmail />
                  </SuspenseRoute>
                } />
                <Route path="/forgot-password" element={
                  <SuspenseRoute>
                    <ForgotPassword />
                  </SuspenseRoute>
                } />
                <Route path="/reset-password" element={
                  <SuspenseRoute>
                    <ResetPassword />
                  </SuspenseRoute>
                } />
                <Route path="/pagar/:paymentReference" element={
                  <SuspenseRoute>
                    <PaymentPage />
                  </SuspenseRoute>
                } />
                <Route path="/aceptar-plan/:acceptanceToken" element={
                  <SuspenseRoute>
                    <AcceptNegotiationPlan />
                  </SuspenseRoute>
                } />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={
                    <SuspenseRoute>
                      <DefaultRedirect />
                    </SuspenseRoute>
                  } />
                  <Route path="dashboard" element={
                    <SuspenseRoute>
                      <Dashboard />
                    </SuspenseRoute>
                  } />
                  <Route path="payments" element={
                    <SuspenseRoute>
                      <PaymentsList />
                    </SuspenseRoute>
                  } />
                  <Route path="historic" element={
                    <SuspenseRoute>
                      <HistoricPayments />
                    </SuspenseRoute>
                  } />
                  <Route path="notifications" element={
                    <SuspenseRoute>
                      <NotificationHistory />
                    </SuspenseRoute>
                  } />
                  <Route path="notification-vectors" element={
                    <SuspenseRoute>
                      <NotificationVectors />
                    </SuspenseRoute>
                  } />
                  <Route path="files" element={
                    <SuspenseRoute>
                      <FilesManager />
                    </SuspenseRoute>
                  } />
                  <Route path="templates" element={
                    <SuspenseRoute>
                      <Templates />
                    </SuspenseRoute>
                  } />
                  <Route path="template-types" element={
                    <SuspenseRoute>
                      <TemplateTypes />
                    </SuspenseRoute>
                  } />
                  <Route path="template-library" element={
                    <SuspenseRoute>
                      <TemplateLibraryPage />
                    </SuspenseRoute>
                  } />
                  <Route path="docs" element={
                    <SuspenseRoute>
                      <SystemDocumentation />
                    </SuspenseRoute>
                  } />
                  <Route path="apidoc" element={
                    <SuspenseRoute>
                      <ApiDocumentation />
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-systems" element={
                    <SuspenseRoute>
                      <PublisherSystems />
                    </SuspenseRoute>
                  } />
                  <Route path="billers" element={
                    <SuspenseRoute>
                      <Billers />
                    </SuspenseRoute>
                  } />
                  <Route path="billers/:biller_id/dashboard" element={
                    <SuspenseRoute>
                      <BillerDashboard />
                    </SuspenseRoute>
                  } />
                  <Route path="customers/:publisher_system_id/:biller_id" element={
                    <SuspenseRoute>
                      <CustomersManagement />
                    </SuspenseRoute>
                  } />
                  <Route path="channels" element={
                    <SuspenseRoute>
                      <Channels />
                    </SuspenseRoute>
                  } />
                  <Route path="profile" element={
                    <SuspenseRoute>
                      <Profile />
                    </SuspenseRoute>
                  } />

                  {/* Collection Score Routes */}
                  <Route path="collection-scores/advanced" element={
                    <SuspenseRoute>
                      <AdvancedScoreDashboard />
                    </SuspenseRoute>
                  } />
                  <Route path="collection-scores/alerts" element={
                    <SuspenseRoute>
                      <AlertsPanel />
                    </SuspenseRoute>
                  } />
                  <Route path="collection-scores/config" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <AdvancedConfigPanel />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* Negotiation Routes */}
                  <Route path="negotiation/plans" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <NegotiationPlansManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="negotiation/campaigns" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <NegotiationCampaignDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* Admin Only Routes */}
                  <Route path="users" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <UserManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />

                  {/* v3.1.0: Publisher User Management - For Publisher Admin */}
                  <Route path="publisher/users" element={
                    <SuspenseRoute>
                      <PublisherUserManagement />
                    </SuspenseRoute>
                  } />

                  {/* v3.1.0: Biller User Management - For Biller Admin */}
                  <Route path="biller/users" element={
                    <SuspenseRoute>
                      <BillerUserManagement />
                    </SuspenseRoute>
                  } />

                  <Route path="roles" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <RoleManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/logs" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <AuditLogsViewer />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/stats" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <AuditStatsDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="audit/security" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <SecurityEventsPanel />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="purge" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <PurgeManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="prices" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <Prices />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="logs" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <Logs />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="admin" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <AdminPanel />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="industries" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <IndustryManagement />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-themes/:publisherId" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <PublisherThemeManager />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="publisher-dashboard/:publisherId" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <PublisherDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                  <Route path="monitoring" element={
                    <SuspenseRoute>
                      <ProtectedRoute requireAdmin={true}>
                        <MonitoringDashboard />
                      </ProtectedRoute>
                    </SuspenseRoute>
                  } />
                </Route>

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: DEFAULT_TOAST_DURATION,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    style: {
                      background: '#10b981',
                    },
                  },
                  error: {
                    duration: 5000,
                    style: {
                      background: '#ef4444',
                    },
                  },
                }}
              />
            </div>
          </ThemeProvider>
        </Router>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
